import { DataContextProvider } from "contexts/DataContext";
import { getTranslations } from "contexts/LocalizationContext/utils";
import { isMagnitudeTemplate, MagnitudePage } from "modules/magnitude/templates/MagnitudePage";
import { StoryblokTemplate } from "modules/storyblok/templates/StoryblokTemplate";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";
import { getCoralCampaign } from "services/coral";
import type { PageProps } from "types/global";
import type { ServerSideParams } from "types/storyblok";
import { getNofollowUrls } from "utils/getNofollowUrls";
import { checkIfValidLanguage } from "utils/localeUtils";
import { getOptimizelyExperiments, getWhiteListDomains } from "utils/serverSideUtils/datasources";
import { getDataFromCMS } from "utils/serverSideUtils/getDataFromCMS";
import { getGlobalComponents } from "utils/serverSideUtils/getGlobalComponents";
import { getServerSideStoryblokPages } from "utils/serverSideUtils/getServerSideStoryblokPages";
import { isPageTrackingless } from "utils/tracking/utils";

const Page = (props: PageProps) => {
  const { serverSideStoryblokPages } = props;

  return (
    <DataContextProvider data={{ serverSideStoryblokPages }}>
      {isMagnitudeTemplate(props) ? <MagnitudePage {...props} /> : <StoryblokTemplate {...props} />}
    </DataContextProvider>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const {
    query,
    req: {
      headers: { referer: pageReferrer = "" },
    },
  } = context;
  const { language } = query as ServerSideParams;

  if (!checkIfValidLanguage(language)) {
    return {
      notFound: true,
    };
  }

  // TODO | Add types after migration getTranslations, getDataFromCMS, etc to TS

  const [
    translations,
    cmsResponse,
    serverSideStoryblokPages,
    whiteListDomains,
    globalComponents,
    optimizelyExperiments,
  ] = await Promise.all<any>([
    getTranslations(language, []),
    getDataFromCMS(context),
    getServerSideStoryblokPages(),
    getWhiteListDomains(),
    getGlobalComponents(language),
    getOptimizelyExperiments(),
  ]);

  if (cmsResponse.errorCode === 404) {
    return {
      notFound: true,
    };
  }

  let coralCampaign = null;

  if (cmsResponse.story?.content.activeCampaign) {
    coralCampaign = await getCoralCampaign(cmsResponse.story?.content.activeCampaign);
  }

  const isTrackingless = isPageTrackingless(query);
  const nofollowUrls = getNofollowUrls(await serverSideStoryblokPages);

  return {
    props: {
      isTrackingless,
      translations,
      language,
      serverSideStoryblokPages,
      whiteListDomains,
      nofollowUrls,
      coralCampaign,
      pageReferrer,
      globalComponents,
      optimizelyExperiments,
      ...cmsResponse,
    },
  };
};
